// @flow

import * as React from 'react';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import Group from 'sail/Group';
import List, {ListItem} from 'sail/List';
import {Heading} from 'docs/components';

import './DocsManifestTable.scss';

// TODO(matv): When we do not have two manifests and agree on the syntax, add typing and
// redo this file. I intentionally avoided doing that now since the docs proxy srv manifest is not 100% complete
// yet.

type DocRowProps = {|
  document: any,
  hideConditionsCol?: boolean,
|};

const DocRow = ({document, hideConditionsCol}: DocRowProps) => {
  if (!document.route || document.route.includes('markdoc')) {
    return null;
  }
  return (
    <DataTableRow>
      <DataTableCell width={400}>
        <a href={document.route}>{document.public_metadata.title}</a>
      </DataTableCell>
      {hideConditionsCol ? null : (
        <DataTableCell>
          {document.conditions?.length > 0 ? 'Yes' : ''}
        </DataTableCell>
      )}
      <DataTableCell>
        <a href={document.route}>{document.route}</a>
      </DataTableCell>
      <DataTableCell width={100}>
        <a href={`http://go/lgdocs/${document.path}`} target="_blank">
          Code
        </a>
        {` | `}
        <a
          href={`https://hubble.corp.stripe.com/viz/dashboards/22588--stripe-docs-stats?tab=Specific+route+based+stats&specific+route=${encodeURIComponent(
            document.route,
          )}`}
          target="_blank"
        >
          Stats
        </a>
      </DataTableCell>
    </DataTableRow>
  );
};

export const DocsManifestTable = ({manifest}: {manifest: any}) => {
  const docs = manifest.filter((doc) => {
    return (
      doc.route &&
      doc.public_metadata &&
      doc.public_metadata.title &&
      !doc.route.includes('markdoc') &&
      !doc.route.includes('style-guide') &&
      !doc.route.includes('templates')
    );
  });
  const hasConditions = docs.filter((doc) => doc.conditions?.length > 0);
  return (
    <Group spacing={8}>
      <List size="small">
        <ListItem>
          Total routes (excl. style guide and markdoc docs): {docs.length}
        </ListItem>
        <ListItem>
          Publicly viewable: {docs.length - hasConditions.length}
        </ListItem>
        <ListItem>
          Conditionally viewable:{' '}
          <a href="/docs/gated">{hasConditions.length}</a>
        </ListItem>
      </List>
      <DataTable className="DocsManifestTable" size="small">
        <DataTableHead
          columns={[
            {label: 'Title'},
            {label: 'Conditional?'},
            {label: 'Route'},
            {label: 'Links'},
          ]}
        />
        <DataTableBody>
          {docs
            .sort((a, b) => {
              return a.public_metadata.title.localeCompare(
                b.public_metadata.title,
              );
            })
            .map((doc) => (
              <DocRow key={doc.route} document={doc} />
            ))}
        </DataTableBody>
      </DataTable>
    </Group>
  );
};

export const DocsManifestTableByCondition = ({manifest}: {manifest: any}) => {
  const groupedByCondition = {};

  manifest
    .filter((doc) => {
      return doc.conditions && doc.route && doc.public_metadata.title;
    })
    .forEach((doc) => {
      doc.conditions.forEach((condition) => {
        groupedByCondition[condition] = groupedByCondition[condition] || [];
        groupedByCondition[condition].push(doc);
      });
    }, {});

  const rows = Object.keys(groupedByCondition)
    .sort()
    .map((condition) => {
      const docs = groupedByCondition[condition];
      return (
        <>
          <DataTableRow background="offset">
            <DataTableCell colSpan={3}>
              <Heading level={4} anchored id={condition}>
                {condition}
              </Heading>
            </DataTableCell>
          </DataTableRow>
          {docs.map((doc) => (
            <DocRow key={doc.route} document={doc} hideConditionsCol />
          ))}
        </>
      );
    });

  return (
    <DataTable className="DocsManifestTable" size="medium">
      <DataTableHead columns={[{label: 'Condition', colSpan: 3}]} />
      <DataTableBody>{rows}</DataTableBody>
    </DataTable>
  );
};
